import type {
  ActurisContactPolicy,
  ActurisPolicyInformation,
} from 'api/acturis/client-center';
import { formatPolicyDate } from './policyGroups';

type Field = { label: string; value?: string | null; wide?: boolean };
type FieldGroup = { title: string; fields: Field[] };

export const formatPolicyPremium = (value?: string | null): string | null => {
  if (value == null || !value.trim() || !Number.isFinite(Number(value)))
    return null;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(Number(value));
};

// These risk fields need product-specific data; summary values are not substitutes.
const riskFields: Record<string, string[]> = {
  'PL AUTO': ['Vehicle Type', 'Vehicles in Business Name', 'Policy Discounts'],
  AUTO: ['Vehicle Type', 'Vehicles in Business Name', 'Policy Discounts'],
  'CL AUTO': ['Fleet', 'Rated Class'],
  'CL PROP': ['Occupancy'],
  'PL LIFE': [
    'Insured',
    'Issue Date',
    'Level To',
    'Renewable',
    'Convertible',
    'Smoker Status',
    'Risk Class',
  ],
};

export const policyInformationGroups = (
  policy: ActurisContactPolicy,
  details: ActurisPolicyInformation | undefined,
  line: string,
  customerId: string,
): FieldGroup[] => {
  const businessType =
    details?.business_event_detail || policy.business_event_detail;
  const commission = details?.commission_rate;
  const commissionValue =
    commission != null &&
    commission.trim() &&
    Number.isFinite(Number(commission))
      ? `${Number(commission)}%`
      : null;
  const groups: FieldGroup[] = [
    {
      title: 'Policy',
      fields: [
        {
          label: 'Policy Number',
          value: details?.policy_number || policy.policy_number,
        },
        { label: 'LOB', value: line },
        {
          label: 'Product',
          value: details?.product_name || policy.product_name,
        },
        { label: 'Product Type', value: details?.product_type },
        { label: 'Province', value: details?.province },
        { label: 'Customer Type', value: details?.customer_type },
        { label: 'Policyholder', value: policy.policyholder_name, wide: true },
        { label: 'Business Type', value: businessType },
        { label: 'Term Length', value: details?.term_length },
        // Keep the list's established dates when both responses provide them.
        {
          label: 'Effective Date',
          value: formatPolicyDate(
            policy.effective_date || details?.effective_date,
          ),
        },
        {
          label: 'Expiry Date',
          value: formatPolicyDate(policy.expiry_date || details?.expiry_date),
        },
        {
          label: 'Policy Inception Date',
          value: formatPolicyDate(
            details?.inception_date || policy.inception_date,
          ),
        },
        {
          label: 'Customer Code',
          value: details?.contact_ref || policy.contact_ref || customerId,
        },
        ...(riskFields[line] || []).map((label) => ({ label })),
        { label: 'Last Update' },
      ],
    },
  ];
  if (line === 'PL HOME') {
    groups.push(
      {
        title: 'Consent & Authorization',
        fields: [
          'Consent Type',
          'Statement',
          'Credit Consent',
          'Credit Date',
        ].map((label) => ({ label })),
      },
      {
        title: 'Insurance History',
        fields: [
          'Property Insured Since',
          'Current Property Insurer',
          'Auto Insured Since',
          'Current Auto Insurer',
          'Broker Since',
          'Multi-Line',
          'Combined',
        ].map((label) => ({ label })),
      },
      {
        title: 'Cancellation History',
        fields: [
          'Non-Payment in Last 6 Years',
          'Number of Cancellations',
          'Last Cancellation Date',
        ].map((label) => ({ label })),
      },
    );
  }
  groups.push(
    {
      title: 'Premium & Payment',
      fields: [
        {
          label: 'Estimated Annual Premium (Expiring)',
          value: formatPolicyPremium(policy.expiring_annual_premium),
        },
        { label: 'Payment Method', value: details?.payment_method },
        { label: 'Payment Plan', value: details?.payment_plan },
      ],
    },
    {
      title: line === 'PL LIFE' ? 'Advisor' : 'Broker',
      fields: [
        { label: 'Branch', value: details?.branch },
        {
          label: line === 'PL LIFE' ? 'Advisor' : 'Producer 1',
          value: details?.producer,
        },
        { label: 'CSR', value: details?.csr },
        // GetContactPolicy describes one version, not both new and renewal rates.
        {
          label:
            businessType === 'New Business'
              ? 'Commission — New'
              : businessType === 'Renewal'
                ? 'Commission — Renewal'
                : 'Commission — Current Version',
          value: commissionValue,
        },
      ],
    },
    {
      title: 'Carrier',
      fields: [
        {
          label: 'Carrier',
          value: details?.carrier_name || policy.carrier_name,
        },
        { label: 'Insurer Account', value: details?.insurer_account },
        { label: 'Contract #' },
      ],
    },
  );
  if (details?.reporting_fields?.length) {
    groups.push({
      title: 'Additional Information',
      fields: details.reporting_fields,
    });
  }
  return groups;
};
